<script lang="ts">
  /**
   * Pick members to put on a manual board — several at a time.
   *
   * ONE CLICK STAGES, ONE BUTTON COMMITS. Selecting used to be two steps for one person:
   * click the row to mark it, then press Add. Clicking a row now stages it outright, the
   * box clears itself for the next name, and the button adds everyone staged — so the
   * shape of the job is search, click, search, click, Add, which is how anyone filling a
   * board actually works.
   *
   * Reuses the members explorer route rather than growing a search endpoint of its own —
   * `GET /v1/communities/{id}/members?q=` is already scoped, indexed and paged, and a
   * second search would be a second definition of who is a member of this community.
   *
   * `staff: 'all'` on purpose. The engine's population excludes staff, so a moderator
   * holds no card on any computed board — but a board someone authored is theirs to fill,
   * and "track my coaches through onboarding" is a normal thing to want.
   */
  import { fetchMembers, type Member, type PipelineStage } from '@api';
  import Avatar from '@ui/Avatar.svelte';

  interface Props {
    skoolId: number;
    /** Columns a member can be dropped into — the board's own, in board order. */
    stages: PipelineStage[];
    /** Pre-selected column, when opened from a specific one's `+ Add`. */
    stageKey?: string | null;
    /** Members who already hold a card here. Offering them is offering a 409: one card
     *  per member per board (`uq_crm_card_member`). Shown but not selectable, because
     *  hiding them reads as "we could not find them" — the one thing the reader would
     *  then be sure of is that their search is broken. */
    alreadyOn?: Set<number>;
    busy?: boolean;
    error?: string | null;
    onpick: (memberIds: number[], stageKey: string) => void;
    oncancel: () => void;
  }

  const { skoolId, stages, stageKey = null, alreadyOn = new Set<number>(),
          busy = false, error = null, onpick, oncancel }: Props = $props();

  const placeable = $derived(stages.filter((s) => !s.hidden));

  let q = $state('');
  let results = $state<Member[]>([]);
  let searching = $state(false);
  let searched = $state(false);
  /** Staged, in the order they were clicked. Holds the whole member so a chip can still
   *  show a name after the search that found them has been typed over. */
  let staged = $state<Member[]>([]);
  // Seeded once: the dialog is mounted fresh per open, and the column someone has
  // chosen in the dropdown must not be reset by the board reloading behind it.
  // svelte-ignore state_referenced_locally
  let target = $state<string>(stageKey ?? stages[0]?.key ?? '');
  let seq = 0;

  const stagedIds = $derived(new Set(staged.map((m) => m.member_id)));

  // 140ms, not 220. The search itself answers in about 40ms against a community of a few
  // hundred, so the debounce WAS most of the wait — long enough to feel like the server
  // was thinking when it had not yet been asked.
  const DEBOUNCE_MS = 140;

  // Sequenced so a slow early response cannot overwrite a fast later one — the stale
  // response race that makes a search box show results for a query already typed over.
  $effect(() => {
    const term = q.trim();
    if (term.length < 2) {
      results = [];
      searched = false;
      searching = false;
      return;
    }
    const mine = ++seq;
    searching = true;
    const t = setTimeout(async () => {
      try {
        const page = await fetchMembers(skoolId, { q: term, staff: 'all', limit: 8 });
        if (mine !== seq) return;
        results = page.items;
        searched = true;
      } catch {
        if (mine === seq) results = [];
      } finally {
        if (mine === seq) searching = false;
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  });

  function stage(m: Member): void {
    if (alreadyOn.has(m.member_id) || stagedIds.has(m.member_id)) return;
    staged = [...staged, m];
    // The box clears itself: the next name is typed straight away, and leaving the old
    // term behind means deleting it first every single time.
    q = '';
    results = [];
    searched = false;
  }

  function unstage(id: number): void {
    staged = staged.filter((m) => m.member_id !== id);
  }

  function submit(): void {
    if (staged.length && target && !busy) onpick(staged.map((m) => m.member_id), target);
  }

  function onkeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') oncancel();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
  }

  /** Enter in the search box stages the first offerable result, so a whole add can be
   *  done without the hand leaving the keyboard. */
  function searchKey(e: KeyboardEvent): void {
    if (e.key !== 'Enter' || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    const first = results.find(
      (m) => !alreadyOn.has(m.member_id) && !stagedIds.has(m.member_id),
    );
    if (first) stage(first);
  }
</script>

<svelte:window on:keydown={onkeydown} />

<div class="fixed inset-0 z-[90] grid place-items-center bg-black/45 p-4"
     role="dialog" aria-modal="true" aria-label="Add members">
  <button class="absolute inset-0" aria-label="Cancel" onclick={oncancel}></button>

  <div class="hair relative w-full max-w-md rounded-box border bg-base-100 p-5 shadow-2xl">
    <p class="eyebrow">Add members</p>

    <label class="mt-3 flex flex-col">
      <span class="label-text mb-1 text-xs font-medium text-base-content/60">Search</span>
      <!-- privacy-ok: the operator's own search term, not a value we hold about a member -->
      <!-- svelte-ignore a11y_autofocus -->
      <input class="input input-bordered input-sm" bind:value={q} autofocus
             onkeydown={searchKey} placeholder="Name or @handle" />
    </label>

    <!-- Sized to the RESULTS, and it does NOT blank while typing. Replacing the list with
         "Searching…" on every keystroke was most of what made this feel slow: the answer
         was already on screen and we took it away to say we were fetching it again. -->
    <div class="mt-2 max-h-56 overflow-y-auto" aria-busy={searching}>
      {#if q.trim().length < 2 && results.length === 0}
        <p class="py-2 text-sm text-base-content/50">Type at least two characters.</p>
      {:else if searched && results.length === 0 && !searching}
        <p class="py-2 text-sm text-base-content/60">Nobody here matches that.</p>
      {:else}
        <ul class="flex flex-col gap-1 transition-opacity {searching ? 'opacity-60' : ''}">
          {#each results as m (m.member_id)}
            {@const on = alreadyOn.has(m.member_id)}
            {@const picked = stagedIds.has(m.member_id)}
            <li>
              <button
                class="hair flex w-full items-center gap-2 rounded-field border border-transparent px-2 py-1.5 text-left
                       {on || picked ? 'cursor-not-allowed opacity-45' : 'hover:bg-base-200/60'}"
                disabled={on || picked}
                onclick={() => stage(m)}
              >
                <Avatar src={m.picture_profile} name={m.name} size={24} memberId={m.member_id} />
                <span class="redact min-w-0 flex-1 truncate text-sm">{m.name}</span>
                {#if on}
                  <span class="shrink-0 text-[0.68rem] text-base-content/55">already on board</span>
                {:else if picked}
                  <span class="shrink-0 text-[0.68rem] text-primary">added below</span>
                {:else if m.handle}
                  <span class="redact truncate text-xs text-base-content/50">@{m.handle}</span>
                {/if}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <!-- The staged set. Present only once something is in it: an empty "To add" rail is
         furniture describing nothing, and the dialog opens with nothing staged. -->
    {#if staged.length > 0}
      <div class="mt-3 border-t hair pt-3">
        <span class="text-xs font-medium text-base-content/60">To add · {staged.length}</span>
        <div class="mt-1.5 flex flex-wrap gap-1.5">
          {#each staged as m (m.member_id)}
            <span class="hair inline-flex items-center gap-1.5 rounded-full border bg-base-100 py-0.5 pl-1 pr-1.5 text-xs">
              <Avatar src={m.picture_profile} name={m.name} size={18} memberId={m.member_id} />
              <span class="redact max-w-[9rem] truncate">{m.name}</span>
              <button class="text-base-content/45 transition-colors hover:text-error"
                      aria-label="Do not add this member"
                      onclick={() => unstage(m.member_id)}>✕</button>
            </span>
          {/each}
        </div>
      </div>
    {/if}

    <label class="mt-3 flex flex-col">
      <span class="label-text mb-1 text-xs font-medium text-base-content/60">Column</span>
      <select class="select select-bordered select-sm" bind:value={target}>
        {#each placeable as s (s.key)}<option value={s.key}>{s.label}</option>{/each}
      </select>
    </label>

    {#if error}<p class="mt-3 text-sm text-error">{error}</p>{/if}

    <div class="mt-5 flex justify-end gap-2">
      <button class="btn btn-ghost btn-sm" onclick={oncancel}>Cancel</button>
      <button class="btn btn-primary btn-sm" onclick={submit}
              disabled={staged.length === 0 || !target || busy}>
        {#if busy}<span class="loading loading-spinner loading-xs"></span>{/if}
        {staged.length > 1 ? `Add ${staged.length} to board` : 'Add to board'}
      </button>
    </div>
  </div>
</div>
